import { ReactElement, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import CityScene, { CameraWaypoint } from "./CityScene";
import ScreenCard from "./AppScreens";

gsap.registerPlugin(ScrollTrigger);

interface Step {
  title: string;
  description: string;
}

interface BeatFeature {
  title: string;
  description: string;
  color: string;
  side: "left" | "right";
  icon: ReactElement;
}

const MapPinIcon = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
    <path
      d="M12 22s7-7.58 7-13a7 7 0 10-14 0c0 5.42 7 13 7 13z"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
    <circle cx="12" cy="9" r="2.4" stroke="currentColor" strokeWidth="1.6" />
  </svg>
);

const ClockIcon = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6" />
    <path d="M12 7v5l3.5 2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

const BellIcon = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
    <path
      d="M6 9a6 6 0 1112 0c0 4 1.5 5.5 1.5 5.5H4.5S6 13 6 9z"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
    <path d="M9.5 17.5a2.5 2.5 0 005 0" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

const CoinIcon = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6" />
    <path
      d="M12 7.5v9M14.5 9.7c0-1-1-1.7-2.5-1.7s-2.5.8-2.5 1.9c0 2.6 5 1.3 5 3.9 0 1.1-1.1 1.9-2.5 1.9s-2.5-.7-2.5-1.7"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </svg>
);

// Shown one at a time, cycling as the van drives down the departure lane in
// the closing beat — the step panel and product screen (which only make
// sense while the freight is mid-journey) are hidden for this stretch.
const BEAT_FEATURES: BeatFeature[] = [
  {
    title: "Live fleet visibility",
    description: "See every truck, container, and shipment on one map, updated the moment something moves.",
    color: "#3edcff",
    side: "right",
    icon: <MapPinIcon />
  },
  {
    title: "Predictive ETAs",
    description: "Arrival times recalculate against live traffic, weather, and port congestion, not a static average.",
    color: "#807fef",
    side: "left",
    icon: <ClockIcon />
  },
  {
    title: "Automatic exception alerts",
    description: "Get pinged the moment a shipment needs attention, so problems surface before they cost you a day.",
    color: "#ff6b81",
    side: "right",
    icon: <BellIcon />
  },
  {
    title: "Route cost intelligence",
    description: "See fuel, toll, and time cost per route before you commit a load to it, not after.",
    color: "#3aa876",
    side: "left",
    icon: <CoinIcon />
  }
];

const STEPS: Step[] = [
  {
    title: "Cleared at the port",
    description:
      "Your container lands and every document is auto-generated and checked against CEISA 4.0 rules before it reaches an inspector — so it leaves the terminal the same day it's discharged."
  },
  {
    title: "Smart rate matching",
    description:
      "We match the load to the carrier and route with the best price and transit time, backed by real performance data, not guesswork. The truck rolls out of the gate already assigned."
  },
  {
    title: "Into the warehouse",
    description:
      "The line-haul truck backs into your distribution hub, and inventory is checked in the moment it hits the dock — no reconciliation days later."
  },
  {
    title: "Delivered to the customer",
    description:
      "Last-mile handoff, tracked door to door. Live status from the dock to the doorstep, so your schedule never has a blind spot."
  }
];

// One waypoint per step boundary (STEPS.length + 1). The camera follows the
// freight: wide over the port, tracking the truck across the city, down into
// the distribution hub, then close on the customer's street.
// The 6th waypoint is the closing "drive-off" beat: camera lifts from the
// tight three-quarter shot on the customer's house up to a near top-down map
// view as the van pulls away down the departure lane.
const WAYPOINTS: CameraWaypoint[] = [
  { position: [-30, 30, 32], lookAt: [-22, 2, 4], zoom: 15 },
  { position: [-10, 26, 26], lookAt: [-6, 1, 0], zoom: 20 },
  { position: [2, 18, 34], lookAt: [6, 2, 16], zoom: 26 },
  { position: [18, 18, 26], lookAt: [22, 1, 6], zoom: 28 },
  { position: [38, 12, 32], lookAt: [42, 2, 15], zoom: 40 },
  { position: [42, 46, 50], lookAt: [42, 0, 46], zoom: 30 }
];

// Cumulative scroll-progress fraction where each step begins/ends. These are
// tuned against the route's arc length so step 3 lands exactly as the truck
// pulls into the distribution hub and hands off to the last-mile van. The
// final break (0.78 -> 1) is the drive-off beat, not a narrative step — the
// route finishes drawing at 0.78 and the last 22% of scroll is purely the
// camera lift + van departure.
const STEP_BREAKS = [0, 0.2, 0.4, 0.58, 0.78, 1];

const ROUTE_COLORS = ["#3edcff", "#807fef", "#9d8cff"];

// Progress fraction where the drive-off beat starts — matches ROUTE_END in
// CityScene.tsx, kept in sync via the last STEP_BREAKS entry rather than a
// second magic number.
const BEAT_START = STEP_BREAKS[STEP_BREAKS.length - 2];

const ProcessSection = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const pinRef = useRef<HTMLDivElement>(null);
  const progressRef = useRef(0);
  const [activeStep, setActiveStep] = useState(0);
  const [beatActive, setBeatActive] = useState(false);
  const [activeFeature, setActiveFeature] = useState(0);

  useEffect(() => {
    if (!containerRef.current || !pinRef.current) return;

    const trigger = ScrollTrigger.create({
      trigger: containerRef.current,
      start: "top top",
      end: "+=700%",
      scrub: 0.6,
      pin: pinRef.current,
      pinSpacing: true,
      onUpdate: (self) => {
        progressRef.current = self.progress;
        let step = STEPS.length - 1;
        for (let i = 0; i < STEP_BREAKS.length - 2; i++) {
          if (self.progress < STEP_BREAKS[i + 1]) {
            step = i;
            break;
          }
        }
        setActiveStep((prev) => (prev !== step ? step : prev));

        const inBeat = self.progress > BEAT_START;
        setBeatActive((prev) => (prev !== inBeat ? inBeat : prev));
        if (inBeat) {
          const du = (self.progress - BEAT_START) / (1 - BEAT_START);
          const raw = du * BEAT_FEATURES.length;
          const featureIndex = Math.min(BEAT_FEATURES.length - 1, Math.floor(raw));
          setActiveFeature((prev) => (prev !== featureIndex ? featureIndex : prev));
        }
      }
    });

    return () => {
      trigger.kill();
    };
  }, []);

  return (
    <Section ref={containerRef}>
      <PinWrapper ref={pinRef}>
        <CanvasWrapper>
          <CityScene
            progressRef={progressRef}
            waypoints={WAYPOINTS}
            breakpoints={STEP_BREAKS}
            routeColorStops={ROUTE_COLORS}
          />
        </CanvasWrapper>

        <ScrollHint style={{ opacity: activeStep === 0 ? 1 : 0 }}>
          SCROLL TO SEE HOW IT WORKS
          <HintLine />
        </ScrollHint>

        {/* Copy on the left, the product screen for that checkpoint on the
            right — read as one row so the screen belongs to the step. Hidden
            during the closing beat: the freight has already been delivered,
            so neither the step list nor the tracking screen still applies. */}
        {!beatActive && (
          <BottomBar>
            <StepPanel>
              {STEPS.map((step, index) => (
                <StepRow key={step.title}>
                  <StepNumber $active={index === activeStep}>
                    {String(index + 1).padStart(2, "0")}
                  </StepNumber>
                  <StepBody>
                    <StepTitle $active={index === activeStep}>{step.title}</StepTitle>
                    {index === activeStep && <StepDescription>{step.description}</StepDescription>}
                  </StepBody>
                </StepRow>
              ))}
            </StepPanel>

            <PanelSlot>
              <ScreenCard activeStep={activeStep} />
            </PanelSlot>
          </BottomBar>
        )}

        {beatActive && (
          <BeatFeaturePanel $side={BEAT_FEATURES[activeFeature].side}>
            <BeatIconBadge $color={BEAT_FEATURES[activeFeature].color}>
              {BEAT_FEATURES[activeFeature].icon}
            </BeatIconBadge>
            <BeatFeatureTitle>{BEAT_FEATURES[activeFeature].title}</BeatFeatureTitle>
            <BeatFeatureDescription>{BEAT_FEATURES[activeFeature].description}</BeatFeatureDescription>
            <BeatDots>
              {BEAT_FEATURES.map((f, i) => (
                <BeatDot key={f.title} $active={i === activeFeature} $color={f.color} />
              ))}
            </BeatDots>
          </BeatFeaturePanel>
        )}
      </PinWrapper>
    </Section>
  );
};

const Section = styled.div`
  position: relative;
  width: 100%;
`;

const PinWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100vh;
  overflow: hidden;
  background: #e6ecf6;
`;

const CanvasWrapper = styled.div`
  position: absolute;
  inset: 0;
`;

/* Copy and product screen share one bottom row and stay bottom-aligned, so
   the card's foot lines up with the last step regardless of copy length. */
const BottomBar = styled.div`
  position: absolute;
  left: 48px;
  right: 48px;
  bottom: 48px;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 40px;
  z-index: 2;
  pointer-events: none;

  @media (max-width: 1025px) {
    left: 24px;
    right: 24px;
  }
`;

const PanelSlot = styled.div`
  flex-shrink: 0;

  @media (max-width: 1180px) {
    display: none;
  }
`;

const ScrollHint = styled.div`
  position: absolute;
  bottom: 48px;
  left: 50%;
  transform: translateX(-50%);
  color: #4b5a76;
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 1px;
  text-align: center;
  transition: opacity 0.4s ease;
  pointer-events: none;

  @media (max-width: 767px) {
    display: none;
  }
`;

const HintLine = styled.div`
  width: 1px;
  height: 32px;
  background: #4b5a76;
  margin: 8px auto 0;
  opacity: 0.5;
`;

const StepPanel = styled.div`
  flex: 1;
  max-width: 560px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 24px 28px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.55);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  pointer-events: none;

  @media (max-width: 1025px) {
    max-width: none;
  }
`;

const StepRow = styled.div`
  display: flex;
  gap: 20px;
  align-items: flex-start;
`;

const StepNumber = styled.div<{ $active: boolean }>`
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  font-weight: 700;
  border-radius: 8px;
  background: ${({ $active }) => ($active ? "#0b1c75" : "transparent")};
  color: ${({ $active }) => ($active ? "#fff" : "#33415c")};
  transition: background 0.3s ease, color 0.3s ease;
`;

const StepBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const StepTitle = styled.p<{ $active: boolean }>`
  margin: 0;
  padding-top: 5px;
  font-size: ${({ $active }) => ($active ? "1.3rem" : "1rem")};
  font-weight: ${({ $active }) => ($active ? 700 : 600)};
  color: ${({ $active }) => ($active ? "#0b1c75" : "#33415c")};
  transition: all 0.3s ease;
`;

const StepDescription = styled.p`
  margin: 0;
  font-size: 0.95rem;
  line-height: 1.5;
  color: #2f3a4d;
  max-width: 480px;
`;

const BeatFeaturePanel = styled.div<{ $side: "left" | "right" }>`
  position: absolute;
  top: 50%;
  ${({ $side }) => ($side === "left" ? "left: 8%;" : "right: 8%;")}
  transform: translateY(-50%);
  width: min(28vw, 360px);
  text-align: ${({ $side }) => ($side === "left" ? "left" : "right")};
  display: flex;
  flex-direction: column;
  align-items: ${({ $side }) => ($side === "left" ? "flex-start" : "flex-end")};
  padding: 24px 28px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.55);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  z-index: 2;
  pointer-events: none;

  @media (max-width: 1025px) {
    display: none;
  }
`;

const BeatIconBadge = styled.div<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  margin-bottom: 16px;
  color: ${({ $color }) => $color};
  background: ${({ $color }) => $color}1f;
  border: 1px solid ${({ $color }) => $color}55;
`;

const BeatFeatureTitle = styled.h3`
  margin: 0 0 10px;
  font-size: 1.3rem;
  font-weight: 700;
  color: #0b1c75;
`;

const BeatFeatureDescription = styled.p`
  margin: 0;
  font-size: 0.95rem;
  line-height: 1.55;
  color: #33415c;
`;

const BeatDots = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 20px;
`;

const BeatDot = styled.div<{ $active: boolean; $color: string }>`
  width: ${({ $active }) => ($active ? "20px" : "7px")};
  height: 7px;
  border-radius: 4px;
  background: ${({ $active, $color }) => ($active ? $color : "rgba(11, 28, 117, 0.18)")};
  transition: all 0.3s ease;
`;

export default ProcessSection;
